import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLp, useT } from "../lib/i18n";

export const COOKIE_CONSENT_STORAGE_KEY = "minuta_cookie_consent_v1";

type CookieConsentChoice = "accepted" | "rejected";

interface StoredCookieConsent {
  version: 1;
  choice: CookieConsentChoice;
  decidedAt: string;
  framework: "LGPD-RGPD";
}

function readStoredConsent(): StoredCookieConsent | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredCookieConsent>;
    if (
      parsed.version === 1 &&
      parsed.framework === "LGPD-RGPD" &&
      (parsed.choice === "accepted" || parsed.choice === "rejected")
    ) {
      return parsed as StoredCookieConsent;
    }
  } catch {
    try {
      window.localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    } catch {
      return null;
    }
  }

  return null;
}

function storeConsent(choice: CookieConsentChoice) {
  if (typeof window === "undefined") return;

  const consent: StoredCookieConsent = {
    version: 1,
    choice,
    decidedAt: new Date().toISOString(),
    framework: "LGPD-RGPD",
  };

  try {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // A blocked storage API should not block the visitor from dismissing the banner.
  }
}

export function CookieConsentBanner() {
  const t = useT();
  const lp = useLp();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(readStoredConsent() === null);
  }, []);

  function decide(choice: CookieConsentChoice) {
    storeConsent(choice);
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <section
      aria-label={t.cookieConsent.ariaLabel}
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-5xl rounded-lg border border-ink/12 bg-paper/95 p-4 text-ink shadow-[0_18px_70px_rgba(10,10,10,0.18)] backdrop-blur-md dark:border-paper/12 dark:bg-ink/95 dark:text-paper sm:inset-x-6 sm:bottom-6 sm:p-5"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="max-w-3xl">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-graphite dark:text-paper/55">
            {t.cookieConsent.kicker}
          </p>
          <h2 className="mt-1 text-base font-semibold leading-tight sm:text-lg">
            {t.cookieConsent.title}
          </h2>
          <p className="mt-2 text-sm leading-6 text-ink/70 dark:text-paper/70">
            {t.cookieConsent.body}{" "}
            <Link
              to={lp + "/privacy"}
              className="font-medium text-ink underline decoration-ink/25 underline-offset-4 hover:decoration-ink dark:text-paper dark:decoration-paper/30 dark:hover:decoration-paper"
            >
              {t.cookieConsent.privacyLink}
            </Link>
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0">
          <button
            type="button"
            onClick={() => decide("rejected")}
            className="inline-flex min-h-11 items-center justify-center rounded-lg border border-ink/14 px-4 text-sm font-medium text-ink/70 transition-colors hover:border-ink/30 hover:text-ink dark:border-paper/16 dark:text-paper/70 dark:hover:border-paper/35 dark:hover:text-paper"
          >
            {t.cookieConsent.reject}
          </button>
          <button
            type="button"
            onClick={() => decide("accepted")}
            className="inline-flex min-h-11 items-center justify-center rounded-lg bg-ink px-4 text-sm font-medium text-paper transition-colors hover:bg-ink/85 dark:bg-paper dark:text-ink dark:hover:bg-paper/90"
          >
            {t.cookieConsent.accept}
          </button>
        </div>
      </div>
    </section>
  );
}
